import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from 'designSystem/lib/utils'
import * as React from 'react'

const alertVariants = cva(
  'relative grid w-full grid-cols-[0_1fr] items-start rounded-md border has-[>svg]:grid-cols-[auto_1fr] [&>svg]:shrink-0 [&>svg]:translate-y-0.5 [&>svg]:text-current',
  {
    variants: {
      variant: {
        default:
          'border-border bg-card text-card-foreground [&>svg]:text-foreground *:data-[slot=alert-description]:text-muted-foreground',
        info: 'border-info/50 bg-info/10 text-info-foreground [&>svg]:text-info *:data-[slot=alert-description]:text-info-foreground',
        success:
          'border-success/50 bg-success/10 text-success-foreground [&>svg]:text-success *:data-[slot=alert-description]:text-success-foreground',
        warning:
          'border-warning/50 bg-warning/10 text-warning-foreground [&>svg]:text-warning *:data-[slot=alert-description]:text-warning-foreground *:data-[slot=alert-title]:text-warning-foreground',
        destructive:
          'border-destructive/50 bg-destructive/5 text-destructive [&>svg]:text-destructive *:data-[slot=alert-description]:text-destructive/90',
      },
      size: {
        default:
          'gap-y-0.5 px-4 py-3 text-sm has-[>svg]:gap-x-3 [&>svg]:size-4',
        sm: 'gap-y-0.5 px-2 py-1.5 text-xs has-[>svg]:gap-x-2 [&>svg]:size-3',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

function Alert({
  className,
  variant = 'default',
  size = 'default',
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot='alert'
      data-variant={variant}
      data-size={size}
      role='alert'
      className={cn(alertVariants({ variant, size }), className)}
      {...props}
    />
  )
}

function AlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='alert-title'
      className={cn(
        'col-start-2 min-h-4 font-medium tracking-tight',
        className
      )}
      {...props}
    />
  )
}

function AlertDescription({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='alert-description'
      className={cn('col-start-2 grid justify-items-start gap-1', className)}
      {...props}
    />
  )
}

export { Alert, AlertDescription, AlertTitle, alertVariants }
